import styles from "./styles.module.scss";
import React, {memo, useMemo} from "react";
import LazyAssets from "@/app/utils/assets_widget";
import {TextBold14, TextBold16, ThirtyText} from "@/app/components/texts";
import {t} from "i18next";
import {s} from "@/app/utils/s";
import Space from "@/app/components/space";
import {NewUserModel} from "@/app/api/user";
import {useNavigate} from "react-router-dom";
import {route} from "@/app/routes/AppRoutes";
import {OnboardingStep} from "@/app/features/onboarding/slice";

interface Props {
  goToOnboarding: () => void
  user?: NewUserModel;
}

const FillOutProfile: React.FC<Props> = ({goToOnboarding, user}) => {
  const navigate = useNavigate();

  const {
    topTitle,
    showImageBlock,
    completedCount,
    totalCount,
    nextStep,
  } = useMemo(() => {
    if (!user) {
      return {
        topTitle: t(s.finishCompletingYourProfile),
        showImageBlock: true,
        completedCount: 0,
        totalCount: 12,
        nextStep: undefined as OnboardingStep | undefined,
      };
    }

    const profile = user;

    const hasImage = !!profile.user?.imageLink;
    const hasRocketSportsExperience = profile.rocketSportsExperience !== undefined && profile.rocketSportsExperience !== null;
    const hasNtrpOrSkill = profile.ntrpRating !== undefined && profile.ntrpRating !== null
      || profile.skillLevel !== undefined && profile.skillLevel !== null;
    const hasCity = !!profile.city;
    const hasGender = !!profile.gender;
    const hasDateOfBirth = !!profile.dateOfBirth;
    const hasPadelClubMembership = !!profile.padelClubMembership;
    const hasPlayingDuration = !!profile.playingDuration;
    const hasPlayingFrequency = !!profile.user.playFrequency;
    const hasAboutYourself = !!profile.profile.aboutYourself && profile.profile.aboutYourself.length > 0;
    const hasIndustry = !!profile.profile.industry && profile.profile.industry.length > 0;
    const hasSocialLinks = (!!profile.profile.instagram && profile.profile.instagram.length > 2)
      || (!!profile.profile.linkedin && profile.profile.linkedin.length > 2);

    const groups: {
      key:
        | "imageLink"
        | "rocketSportsExperience"
        | "ntrpOrSkill"
        | "city"
        | "gender"
        | "dateOfBirth"
        | "padelClubMembership"
        | "playingDuration"
        | "playingFrequency"
        | "aboutYourself"
        | "industry"
        | "socialLinks";
      isFilled: boolean;
    }[] = [
      {key: "imageLink", isFilled: hasImage},
      {key: "rocketSportsExperience", isFilled: hasRocketSportsExperience},
      {key: "ntrpOrSkill", isFilled: hasNtrpOrSkill},
      {key: "city", isFilled: hasCity},
      {key: "gender", isFilled: hasGender},
      {key: "dateOfBirth", isFilled: hasDateOfBirth},
      {key: "padelClubMembership", isFilled: hasPadelClubMembership},
      {key: "playingDuration", isFilled: hasPlayingDuration},
      {key: "playingFrequency", isFilled: hasPlayingFrequency},
      {key: "aboutYourself", isFilled: hasAboutYourself},
      {key: "industry", isFilled: hasIndustry},
      {key: "socialLinks", isFilled: hasSocialLinks},
    ];

    console.log(hasPlayingFrequency, hasAboutYourself, hasIndustry, hasSocialLinks);

    const total = groups.length;
    const completed = groups.filter(g => g.isFilled).length;

    let titleKey: string = s.finishCompletingYourProfile;
    let step: OnboardingStep | undefined = undefined;

    const firstEmpty = groups.find(g => !g.isFilled);
    if (firstEmpty) {
      switch (firstEmpty.key) {
        case "imageLink":
          titleKey = s.firstThingsFirst;
          step = OnboardingStep.FIRST_THINGS_FIRST;
          break;
        case "rocketSportsExperience":
          titleKey = s.racketSportsExperience;
          step = OnboardingStep.EXPERIENCE;
          break;
        case "ntrpOrSkill":
          titleKey = s.haveNprpRating;
          step = OnboardingStep.HAS_NPRP_RATING;
          break;
        case "city":
          titleKey = s.cityPlayIn;
          step = OnboardingStep.CITY;
          break;
        case "gender":
          titleKey = s.gender;
          step = OnboardingStep.GENDER;
          break;
        case "dateOfBirth":
          titleKey = s.dateOfBirth;
          step = OnboardingStep.DATE_OF_BIRTH;
          break;
        case "padelClubMembership":
          titleKey = s.memberOfPadelClub;
          step = OnboardingStep.MEMBERSHIP;
          break;
        case "playingDuration":
          titleKey = s.howLongPlaying;
          step = OnboardingStep.LONG_TERM;
          break;
        case "playingFrequency":
          titleKey = s.howOftenPlayPadel;
          step = OnboardingStep.OFTEN;
          break;
        case "aboutYourself":
          titleKey = s.anythingToAdd;
          step = OnboardingStep.ABOUT;
          break;
        case "industry":
          titleKey = s.addIndustry;
          step = OnboardingStep.INDUSTRY;
          break;
        case "socialLinks":
          titleKey = s.addSocialLinks;
          step = OnboardingStep.SOCIAL_LINKS;
          break;
        default:
          titleKey = s.finishCompletingYourProfile;
      }
    }

    const showImage = !hasImage;

    return {
      topTitle: t(titleKey),
      showImageBlock: showImage,
      completedCount: completed,
      totalCount: total,
      nextStep: step,
    };
  }, [user]);

  // If all groups are filled, we don't need to show the prompt widget at all.
  if (user && completedCount === totalCount) {
    return null;
  }

  const handleClick = () => {
    if (nextStep !== undefined) {
      navigate(`${route.onboarding}?step=${nextStep}`);
    } else {
      navigate(route.onboarding);
    }
  };

  return (
    <div className={styles.fillOutProfile} onClick={handleClick}>
      <div className={styles.titles}>
        <ThirtyText text={t(s.fillOutYourProfile)}/>
        <TextBold16 text={t(s.helpsToMatchWithOpponents)}/>
      </div>

      <div className={styles.blocks}>
        <div className={styles.block}>
          {showImageBlock && <LazyAssets.Image/>}
          {showImageBlock && <Space.W8/>}
          <TextBold14 text={topTitle}/>
          <div className={styles.space}></div>
          <LazyAssets.Add/>
        </div>
        <div className={styles.block}>
          <TextBold14 text={t(s.finishCompletingYourProfile)}/>
          <div className={styles.space}></div>
          <TextBold14 text={`${completedCount}/${totalCount}`} isGray/>
          <Space.W8/>
          <LazyAssets.ChevronRight/>
        </div>
      </div>
    </div>
  );
};

export default memo(FillOutProfile);